import { useCallback } from 'react';
import { arrayUnion, doc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import type { CollectionReference, DocumentData, DocumentReference } from 'firebase/firestore';
import { toast } from '@/hooks/use-toast';
// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};
import type { Commit, Customer, Expense, JournalEntry, TimeEntry } from '@/pages/developer/types';

const DEFAULT_HOURLY_RATE = 65;

type FinanceActionsParams = {
  commits: Commit[];
  expenses: Expense[];
  journalEntries: JournalEntry[];
  availableCustomers: Customer[];
  timeEntries: TimeEntry[];
  firestoreCommitsCollectionRef: CollectionReference<DocumentData> | null;
  firestoreDocRef: DocumentReference<DocumentData> | null;
  ratePerCommit: number;
  retainerPaid: number;
  repoOwner: string;
  repoName: string;
  internalCustomerName: string;
  customerCreateSentinel: string;
  actorId: string;
  internalCustomerId: string;
  developerUnlocked: boolean;
  isAdminUser: boolean;
  resolveCustomerLabel: (customerId: string) => string | null;
  generateId: () => string;
  timeDate: string;
  timeHours: string;
  timeHourlyRate: string;
  timeDescription: string;
  gitOrderDescription: string;
  timeMisc: string;
  timeTaxes: string;
  timeEntryCommitShas: string[];
  timeEntryMarkCommitsComplete: boolean;
  expenseDate: string;
  expenseCategory: string;
  expenseAmount: string;
  expenseMisc: string;
  expenseTaxes: string;
  expenseNotes: string;
  expenseJournalEntry: string;
  expenseAttachment: File | null;
  journalDate: string;
  journalType: JournalEntry['entryType'];
  journalCustomerId: string;
  journalAmount: string;
  journalMisc: string;
  journalTaxes: string;
  journalNotes: string;
  setDeveloperDialogOpen: (value: boolean) => void;
  setCommits: (updater: (prev: Commit[]) => Commit[]) => void;
  setTimeEntries: (value: TimeEntry[]) => void;
  setTimeDate: (value: string) => void;
  setTimeHours: (value: string) => void;
  setTimeDescription: (value: string) => void;
  setGitOrderDescription: (value: string) => void;
  setTimeMisc: (value: string) => void;
  setTimeTaxes: (value: string) => void;
  setTimeEntryCommitShas: (value: string[]) => void;
  setTimeEntryMarkCommitsComplete: (value: boolean) => void;
  setExpenses: (value: Expense[]) => void;
  setExpenseDate: (value: string) => void;
  setExpenseCategory: (value: string) => void;
  setExpenseAmount: (value: string) => void;
  setExpenseMisc: (value: string) => void;
  setExpenseTaxes: (value: string) => void;
  setExpenseNotes: (value: string) => void;
  setExpenseJournalEntry: (value: string) => void;
  setExpenseAttachment: (value: File | null) => void;
  setJournalEntries: (value: JournalEntry[]) => void;
  setJournalDate: (value: string) => void;
  setJournalType: (value: JournalEntry['entryType']) => void;
  setJournalCustomerId: (value: string) => void;
  setJournalAmount: (value: string) => void;
  setJournalMisc: (value: string) => void;
  setJournalTaxes: (value: string) => void;
  setJournalNotes: (value: string) => void;
};

type FinanceActionsReturn = {
  handleAddTimeEntry: () => Promise<void>;
  createGitOrderFromSelectedCommits: () => Promise<void>;
  handleAddExpense: () => Promise<void>;
  handleAddJournalEntry: () => Promise<void>;
};

export const useDeveloperFinanceActions = (params: FinanceActionsParams): FinanceActionsReturn => {
  const handleAddTimeEntry = useCallback(async () => {
    if (!params.timeDate || !params.timeHours || !params.timeDescription) {
      toast({ title: 'Missing details', description: 'Add a date, hours, and description.' });
      return;
    }

    const hourlyRateNumber = params.timeHourlyRate.trim() ? Number(params.timeHourlyRate) : DEFAULT_HOURLY_RATE;
    if (!Number.isFinite(hourlyRateNumber) || hourlyRateNumber < 0) {
      toast({ title: 'Invalid rate', description: 'Hourly rate must be a valid number.' });
      return;
    }

    const entry: TimeEntry = {
      id: params.generateId(),
      date: new Date(params.timeDate).toISOString(),
      hours: Number(params.timeHours),
      hourlyRate: hourlyRateNumber,
      description: params.timeDescription,
      billable: true,
      kind: 'time-ticket',
      gitOrderId: null,
      stage: 'workorder',
      invoiceJournalEntryId: null,
    };
    const nextEntries = [entry, ...params.timeEntries];

    params.setTimeEntries(nextEntries);
    params.setTimeDate('');
    params.setTimeHours('');
    params.setTimeDescription('');

    if (!params.firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Connect Firestore to sync time tickets.' });
      return;
    }

    try {
      await updateDoc(params.firestoreDocRef, { timeEntries: arrayUnion(entry) });
    } catch {
      await setDoc(
        params.firestoreDocRef,
        {
          timeEntries: nextEntries,
          commits: params.commits,
          expenses: params.expenses,
          retainerPaid: params.retainerPaid,
          ratePerCommit: params.ratePerCommit,
          repoOwner: params.repoOwner,
          repoName: params.repoName,
          journalEntries: params.journalEntries,
        },
        { merge: true }
      );
    }
  }, [params]);

  const createGitOrderFromSelectedCommits = useCallback(async () => {
    const selectedCommitShas = params.timeEntryCommitShas.map((sha) => sha.trim()).filter(Boolean);

    const miscNumber = params.timeMisc.trim() ? Number(params.timeMisc) : 0;
    const taxesNumber = params.timeTaxes.trim() ? Number(params.timeTaxes) : 0;
    if ((params.timeMisc.trim() && !Number.isFinite(miscNumber)) || (params.timeTaxes.trim() && !Number.isFinite(taxesNumber))) {
      toast({ title: 'Invalid totals', description: 'Misc and Taxes must be valid numbers.' });
      return;
    }

    if (!params.timeDate || selectedCommitShas.length === 0) {
      toast({ title: 'Missing details', description: 'Select a date and at least one commit.' });
      return;
    }

    const description = params.gitOrderDescription.trim() || `Git order (${params.timeDate})`;

    const entry: TimeEntry = {
      id: params.generateId(),
      date: new Date(params.timeDate).toISOString(),
      hours: 0,
      hourlyRate: DEFAULT_HOURLY_RATE,
      description,
      billable: true,
      kind: 'git-order',
      stage: 'workorder',
      commitShas: selectedCommitShas,
      commitRate: params.ratePerCommit,
      misc: miscNumber ? miscNumber : undefined,
      taxes: taxesNumber ? taxesNumber : undefined,
      invoiceJournalEntryId: null,
    };

    const nextEntries = [entry, ...params.timeEntries];
    params.setTimeEntries(nextEntries);
    params.setGitOrderDescription('');
    params.setTimeMisc('');
    params.setTimeTaxes('');
    params.setTimeEntryCommitShas([]);
    params.setTimeEntryMarkCommitsComplete(true);

    const nextStatus: Commit['workStatus'] = params.timeEntryMarkCommitsComplete ? 'completed' : 'assigned';
    params.setCommits((prev) =>
      prev.map((commit) =>
        selectedCommitShas.includes(commit.sha)
          ? {
              ...commit,
              workStatus: nextStatus,
              workorderId: entry.id,
            }
          : commit
      )
    );

    const db = await getDb();
    if (db && params.firestoreCommitsCollectionRef) {
      try {
        let batch = writeBatch(db);
        let ops = 0;
        for (const sha of selectedCommitShas) {
          batch.update(doc(params.firestoreCommitsCollectionRef, sha), {
            workStatus: nextStatus,
            workorderId: entry.id,
          });
          ops += 1;
          if (ops >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            ops = 0;
          }
        }
        if (ops > 0) await batch.commit();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to update commit status.';
        toast({ title: 'Commit update failed', description: message });
      }
    }

    if (!params.firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Connect Firestore to sync Git orders.' });
      return;
    }

    try {
      await updateDoc(params.firestoreDocRef, { timeEntries: arrayUnion(entry) });
    } catch {
      await setDoc(
        params.firestoreDocRef,
        {
          timeEntries: nextEntries,
          commits: params.commits,
          expenses: params.expenses,
          retainerPaid: params.retainerPaid,
          ratePerCommit: params.ratePerCommit,
          repoOwner: params.repoOwner,
          repoName: params.repoName,
          journalEntries: params.journalEntries,
        },
        { merge: true }
      );
    }

    toast({ title: 'Git order created', description: `Created a Git order for ${selectedCommitShas.length} commits.` });
  }, [params]);

  const handleAddExpense = useCallback(async () => {
    if (!params.expenseDate || !params.expenseCategory || !params.expenseAmount) {
      toast({ title: 'Missing details', description: 'Add a date, category, and amount.' });
      return;
    }

    const miscNumber = params.expenseMisc.trim() ? Number(params.expenseMisc) : 0;
    const taxesNumber = params.expenseTaxes.trim() ? Number(params.expenseTaxes) : 0;
    if ((params.expenseMisc.trim() && !Number.isFinite(miscNumber)) || (params.expenseTaxes.trim() && !Number.isFinite(taxesNumber))) {
      toast({ title: 'Invalid totals', description: 'Misc and Taxes must be valid numbers.' });
      return;
    }

    const entry: Expense = {
      id: params.generateId(),
      date: new Date(params.expenseDate).toISOString(),
      category: params.expenseCategory,
      amount: Number(params.expenseAmount),
      misc: miscNumber ? miscNumber : undefined,
      taxes: taxesNumber ? taxesNumber : undefined,
      notes: params.expenseNotes || undefined,
      journalEntry: params.expenseJournalEntry || undefined,
      attachmentName: params.expenseAttachment?.name,
      attachmentType: params.expenseAttachment?.type,
    };
    const nextExpenses = [entry, ...params.expenses];

    params.setExpenses(nextExpenses);
    params.setExpenseDate('');
    params.setExpenseCategory('');
    params.setExpenseAmount('');
    params.setExpenseMisc('');
    params.setExpenseTaxes('');
    params.setExpenseNotes('');
    params.setExpenseJournalEntry('');
    params.setExpenseAttachment(null);

    if (!params.firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Connect Firestore to sync expenses.' });
      return;
    }

    try {
      await updateDoc(params.firestoreDocRef, { expenses: arrayUnion(entry) });
    } catch {
      await setDoc(
        params.firestoreDocRef,
        {
          expenses: nextExpenses,
          commits: params.commits,
          timeEntries: params.timeEntries,
          retainerPaid: params.retainerPaid,
          ratePerCommit: params.ratePerCommit,
          repoOwner: params.repoOwner,
          repoName: params.repoName,
          journalEntries: params.journalEntries,
        },
        { merge: true }
      );
    }
  }, [params]);

  const handleAddJournalEntry = useCallback(async () => {
    if (!params.developerUnlocked && !params.isAdminUser) {
      toast({ title: 'Developer unlock required', description: 'Enter the developer passcode to add journal entries.' });
      params.setDeveloperDialogOpen(true);
      return;
    }

    const selectedCustomerIdRaw = (params.journalCustomerId ?? '').trim();
    const selectedCustomerId = selectedCustomerIdRaw === params.customerCreateSentinel ? '' : selectedCustomerIdRaw;
    if (!params.journalDate || !selectedCustomerId || !params.journalAmount) {
      toast({ title: 'Missing details', description: 'Add a date, customer, and amount.' });
      return;
    }

    if (!params.availableCustomers.some((customer) => customer.id === selectedCustomerId)) {
      toast({ title: 'Customer not found', description: 'Select an existing customer or create a new one.' });
      return;
    }

    const miscNumber = params.journalMisc.trim() ? Number(params.journalMisc) : 0;
    const taxesNumber = params.journalTaxes.trim() ? Number(params.journalTaxes) : 0;
    if ((params.journalMisc.trim() && !Number.isFinite(miscNumber)) || (params.journalTaxes.trim() && !Number.isFinite(taxesNumber))) {
      toast({ title: 'Invalid totals', description: 'Misc and Taxes must be valid numbers.' });
      return;
    }

    const customerLabel = params.resolveCustomerLabel(selectedCustomerId) || params.internalCustomerName;
    const entry: JournalEntry = {
      id: params.generateId(),
      date: new Date(params.journalDate).toISOString(),
      entryType: params.journalType,
      customerId: selectedCustomerId,
      customer: customerLabel,
      amount: Number(params.journalAmount),
      misc: miscNumber ? miscNumber : undefined,
      taxes: taxesNumber ? taxesNumber : undefined,
      notes: params.journalNotes || undefined,
      createdBy: params.actorId,
    };
    const nextEntries = [entry, ...params.journalEntries];

    params.setJournalEntries(nextEntries);
    params.setJournalDate('');
    params.setJournalAmount('');
    params.setJournalMisc('');
    params.setJournalTaxes('');
    params.setJournalNotes('');

    if (!params.firestoreDocRef) {
      toast({ title: 'Saved locally', description: 'Connect Firestore to sync journal entries.' });
      return;
    }

    try {
      await updateDoc(params.firestoreDocRef, { journalEntries: arrayUnion(entry) });
    } catch {
      await setDoc(
        params.firestoreDocRef,
        {
          journalEntries: nextEntries,
          commits: params.commits,
          timeEntries: params.timeEntries,
          expenses: params.expenses,
          retainerPaid: params.retainerPaid,
          ratePerCommit: params.ratePerCommit,
          repoOwner: params.repoOwner,
          repoName: params.repoName,
        },
        { merge: true }
      );
    }
  }, [params]);

  return {
    handleAddTimeEntry,
    createGitOrderFromSelectedCommits,
    handleAddExpense,
    handleAddJournalEntry,
  };
};
